import {
  EEventContextAction,
  EEventType,
  type IMessage,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, {
  useActionState,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import PrivacyNoticeLink from '../fragments/privacy-notice-link';
import { eventSend } from '../reducers/event/reducer';
import { sendMessage } from '../reducers/messenger/reducer';
import { type IMessengerState } from '../reducers/messenger/types';

function MessengerComponent({
  dispatch,
  Messenger,
}: {
  dispatch: Dispatch;
  Messenger: IMessengerState;
}): React.JSX.Element {
  const { t } = useTranslation();

  const [isOpened, setOpened] = useState(false);
  const [unReaded, setUnreaded] = useState(-1);
  const messageRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const [, formAction, isFormPending] = useActionState(
    async (_prevState: unknown, formData: FormData): Promise<null> => {
      const message = formData.get('message') as string;
      if (!message?.trim()) return null;
      setUnreaded(0);
      dispatch(sendMessage({ message }));
      return null;
    },
    null,
  );

  // Reset form input after server confirms message sent
  useEffect((): void => {
    if (!Messenger.inProgress && !Messenger.message) {
      formRef.current?.reset();
    }
  }, [Messenger.inProgress, Messenger.message]);

  const setMessengerState = useCallback(
    (isOpen: boolean): void => {
      setOpened(isOpen);
      dispatch(
        eventSend({
          type: EEventType.MESSENGER_WINDOW_OPEN,
          action: EEventContextAction.CLICK,
          context: { isEnabled: isOpen },
        }),
      );
    },
    [setOpened, dispatch],
  );

  useEffect((): void => {
    messageRef?.current?.scrollIntoView();
  }, [Messenger.lastMessageUUID, isOpened]);

  useEffect((): void => {
    setUnreaded((count: number): number => count + 1);
  }, [Messenger.lastMessageUUID]);

  useEffect((): void => {
    if (unReaded > 0) {
      const audio = new Audio(`/static/juntos.mp3`);

      if (audio) {
        audio.play();
      }
    }
  }, [unReaded]);

  return (
    <div className={`messenger ${isOpened ? 'opened' : 'closed'}`}>
      <button
        type="button"
        tabIndex={isOpened ? -1 : 0}
        aria-label="Open chat"
        className="messenger--icon"
        onClick={(): void => setMessengerState(true)}
      >
        <svg aria-hidden="true" focusable="false">
          <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z" />
        </svg>
      </button>

      <button
        type="button"
        tabIndex={isOpened ? 0 : -1}
        aria-label="Close chat"
        className={`messenger--closeIcon ${isOpened ? 'opened' : 'closed'}`}
        onClick={(): void => {
          setMessengerState(false);
          setUnreaded(0);
        }}
      >
        <svg aria-hidden="true" focusable="false">
          <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.59-13L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z" />
        </svg>
      </button>

      <div
        aria-live="polite"
        aria-atomic="true"
        className={`messenger--messageCount ${
          !isOpened && unReaded > 0 ? 'show' : 'hide'
        }`}
      >
        {unReaded > 0 ? unReaded : ''}
      </div>

      <div
        aria-hidden="true"
        className={`messenger--isAvailable ${
          Messenger.isAvailable ? 'yes' : 'no'
        }`}
      />

      <div
        className={`messenger--content ${isOpened ? 'opened' : 'closed'}`}
        aria-live="polite"
        aria-label="Chat messages"
      >
        {Messenger.messages.map(
          ({ message, direction, uuid }: IMessage): React.JSX.Element => (
            <div
              key={uuid}
              className={`messenger--message ${direction}`}
              ref={messageRef}
            >
              {message}
            </div>
          ),
        )}
      </div>
      <div
        className={`messenger--write-a-message ${
          isOpened ? 'opened' : 'closed'
        }`}
      >
        <form ref={formRef} action={formAction}>
          <input
            placeholder={t('messenger.writeAMessage')}
            name="message"
            type="text"
            disabled={isFormPending || Messenger.inProgress}
            id="messenger-message"
            aria-label={t('messenger.writeAMessage')}
          />
        </form>
        <PrivacyNoticeLink />
      </div>
    </div>
  );
}

const mapStateToProps = ({ Messenger }) => ({
  Messenger,
});

export default connect(mapStateToProps)(MessengerComponent);
